import QRCode from "qrcode";
import { Button, Column, Image, Panel, Row, TextInput, Text, UIFinal, signal, when } from "yage/ui/final";
import { megabytes, offlineState, saveOffline } from "../services/offline-pack.js";

export interface LobbyChoice {
    mode: "solo" | "co-op" | "host" | "join" | "offline";
    /** Solo only: start a new career instead of continuing the saved one. */
    newGame?: boolean;
    captains?: number;
    code?: string;
}

const CODE_ALPHABET = "234579ACDEFGHJKMNPQRTWXYZ";

export function randomCode(): string {
    const bytes = crypto.getRandomValues(new Uint8Array(6));
    return Array.from(bytes, byte => CODE_ALPHABET[byte % CODE_ALPHABET.length]).join("");
}

async function inviteUrl(code: string): Promise<string> {
    const url = new URL(location.href);
    if (url.hostname === "game.sprkt.xyz") url.protocol = "https:";
    if (url.hostname === "localhost" || url.hostname === "127.0.0.1") {
        const response = await fetch("/__sky_harbor/network");
        if (!response.ok) throw new Error("Could not find this computer’s local network address.");
        const { address } = await response.json() as { address: string };
        if (address === "127.0.0.1") throw new Error("Connect this computer to a local network to share a room.");
        url.hostname = address;
    }
    const peer = url.searchParams.get("peer");
    url.search = "";
    url.searchParams.set("join", code);
    if (peer) url.searchParams.set("peer", peer);
    return url.href;
}

export function showLobby(onStart: (choice: LobbyChoice) => Promise<void>, invitedCode: string | null = null, savedGame: Promise<boolean> = Promise.resolve(false)): UIFinal {
    const view = signal<"menu" | "multiplayer" | "confirm-new">(invitedCode ? "multiplayer" : "menu");
    const hasSave = signal(false);
    const code = signal(randomCode());
    const joinCode = signal(invitedCode ?? "");
    const link = signal("");
    const qr = signal("");
    const error = signal("");
    const note = signal("");
    const busy = signal(false);
    const ready = signal(false);
    const hosting = signal(!invitedCode);
    const captains = signal(2);
    const offline = signal("");
    const offlineNote = signal("");
    const offlineBusy = signal(false);
    const ui = new UIFinal();
    void savedGame.then(saved => hasSave.set(saved), () => hasSave.set(false));

    const refreshOffline = async () => {
        const status = await offlineState();
        if (status.kind === "unsupported") { offline.set(""); return; }
        if (status.kind === "missing") { offline.set("Save for offline" + (status.bytes ? ` (${megabytes(status.bytes)})` : "")); offlineNote.set(""); return; }
        offline.set(status.current === false ? "Update offline copy" : "Saved for offline ✓");
        offlineNote.set(status.current === null ? "Offline copy ready. No internet now, so it could not check for updates." : "");
        // A saved copy follows new builds by itself while online, so both phones stay on the same version.
        if (status.current === false) void storeOffline();
    };
    const storeOffline = async () => {
        if (offlineBusy.get()) return;
        offlineBusy.set(true);
        offlineNote.set("");
        try {
            await saveOffline(fraction => offline.set(`Saving for offline… ${Math.floor(fraction * 100)}%`));
            offline.set("Saved for offline ✓");
            offlineNote.set("This device can now open the game with no internet. Do the same on the other phone.");
        } catch (cause) {
            offline.set("Save for offline");
            offlineNote.set(cause instanceof Error ? cause.message : "Could not save for offline play.");
        } finally {
            offlineBusy.set(false);
        }
    };
    void refreshOffline();

    const begin = async (choice: LobbyChoice, stayForInvite = false) => {
        if (busy.get() || ready.get()) return;
        busy.set(true);
        error.set("");
        note.set(choice.mode === "solo" || choice.mode === "co-op" ? "Preparing your airport…" : choice.mode === "host" ? "Opening your room…" : choice.mode === "offline" ? "" : "Connecting…");
        try {
            await onStart(choice);
            if (stayForInvite) {
                ready.set(true);
                note.set("Room ready. Share the QR code, then enter your airport.");
                return;
            }
            ui.dispose();
        } catch (cause) {
            error.set(cause instanceof Error ? cause.message : String(cause || "Could not connect to the room."));
            note.set("");
        } finally {
            busy.set(false);
        }
    };

    const host = async () => {
        if (busy.get() || ready.get()) return;
        view.set("multiplayer");
        hosting.set(true);
        busy.set(true);
        error.set("");
        note.set("Preparing invite…");
        try {
            const url = await inviteUrl(code.get());
            link.set(url);
            qr.set(await QRCode.toDataURL(url, { width: 240, margin: 2, errorCorrectionLevel: "M" }));
        } catch (cause) {
            error.set(cause instanceof Error ? cause.message : "Could not create an invite.");
            note.set("");
            busy.set(false);
            return;
        }
        busy.set(false);
        await begin({ mode: "host", code: code.get() }, true);
    };

    const join = () => {
        const typed = joinCode.get().trim().toUpperCase();
        if (!/^[234579ACDEFGHJKMNPQRTWXYZ]{6}$/.test(typed)) {
            error.set("Enter the six-character code from your friend’s invite.");
            return;
        }
        void begin({ mode: "join", code: typed });
    };

    ui.mount(
        Panel({ style: {
            position: "absolute", inset: 0, display: "grid", placeItems: "center",
            background: "url(assets/art/directions/south/airport-menu.webp) center / cover no-repeat, #10213f",
            overflowY: "auto", padding: 16,
        } },
            Column({ style: {
                alignItems: "center", gap: 14, padding: 24, width: "min(100%, 440px)", boxSizing: "border-box",
                background: "rgba(16, 33, 63, 0.94)", borderRadius: 14, color: "#ffffff",
            } },
                Text("SKY HARBOR", {
                    role: "heading", attrs: { "aria-level": 1 },
                    style: { fontSize: 36, color: "#ffffff", letterSpacing: 3, textAlign: "center" },
                }),
                Text("Build your airport. Play solo or with friends.", { style: { fontSize: 14, color: "#b5ddd5", textAlign: "center" } }),
                when(() => error.get() !== "", () => Text(() => error.get(), { style: { fontSize: 13, color: "#ff9c9c", textAlign: "center" } })),
                Text(() => note.get() || ' ', { style: { minHeight: 20, fontSize: 13, color: "#a4e7ce", textAlign: "center" }, attrs: { 'aria-live': 'polite' } }),
                when(() => view.get() === "menu", () =>
                    Column({ style: { alignItems: "stretch", gap: 12, width: "100%" } },
                        when(() => hasSave.get(), () => Button({ style: { minHeight: 54, fontSize: 19 }, on: { activate: () => { void begin({ mode: "solo" }); } } }, "Continue")),
                        Button({ style: { minHeight: 54, fontSize: 19 }, on: { activate: () => { if (hasSave.get()) { view.set("confirm-new"); error.set(""); } else void begin({ mode: "solo", newGame: true }); } } }, "New game"),
                        Button({ style: { minHeight: 54, fontSize: 19 }, on: { activate: () => { void host(); } } }, "Multiplayer"),
                        Text("Friends can join from their own phone or computer.", { style: { fontSize: 12, color: "#b5ddd5", textAlign: "center" } }),
                        Button({ style: { minHeight: 48, fontSize: 17 }, on: { activate: () => { void begin({ mode: "offline" }); } } }, "Play offline together"),
                        when(() => offline.get() !== "", () => Button({ style: { minHeight: 40 }, on: { activate: () => { void storeOffline(); } } }, Text(() => offline.get()))),
                        when(() => offlineNote.get() !== "", () => Text(() => offlineNote.get(), { style: { fontSize: 12, color: "#b5ddd5", textAlign: "center" } })),
                        Row({ style: { gap: 8, justifyContent: "center", alignItems: "center", flexWrap: "wrap" } },
                            Text("Same screen:", { style: { fontSize: 12, color: "#b5ddd5" } }),
                            ...[2, 3, 4].map(count => Button({
                                style: { minWidth: 38, minHeight: 36, background: () => captains.get() === count ? "#1d3a33" : "#172232" },
                                on: { activate: () => captains.set(count) },
                            }, String(count))),
                            Button({ style: { minHeight: 36 }, on: { activate: () => { void begin({ mode: "co-op", captains: captains.get() }); } } }, "Play"),
                        ),
                    )),
                when(() => view.get() === "confirm-new", () =>
                    Column({ style: { alignItems: "stretch", gap: 12, width: "100%" } },
                        Text("Start a new game? Your saved airport on this device will be replaced.", { style: { fontSize: 15, color: "#ffffff", textAlign: "center" } }),
                        Button({ style: { minHeight: 50, fontSize: 17 }, on: { activate: () => { void begin({ mode: "solo", newGame: true }); } } }, "Start new game"),
                        Button({ style: { minHeight: 40 }, on: { activate: () => { view.set("menu"); error.set(""); } } }, "Back"),
                    )),
                when(() => view.get() === "multiplayer" && hosting.get(), () =>
                    Column({ style: { alignItems: "center", gap: 12, width: "100%" } },
                        Text("INVITE A FRIEND", { style: { fontSize: 14, letterSpacing: 2, color: "#a4e7ce" } }),
                        when(() => ready.get(), () => Image({ src: () => qr.get(), alt: "QR code to join this airport", style: { width: 220, height: 220, background: "white", padding: 6 } })),
                        when(() => ready.get(), () => Text(() => code.get(), { style: { fontSize: 27, letterSpacing: 5, color: "#ffffff" } })),
                        when(() => ready.get(), () => Text(() => link.get(), { style: { fontSize: 12, color: "#b5ddd5", overflowWrap: "anywhere", textAlign: "center" } })),
                        when(() => ready.get(), () => Button({ style: { minHeight: 48, width: "100%" }, on: { activate: () => ui.dispose() } }, "Enter airport")),
                        when(() => !ready.get() && !busy.get(), () => Button({ style: { minHeight: 42 }, on: { activate: () => { void host(); } } }, "Try again")),
                    )),
                when(() => view.get() === "multiplayer" && !hosting.get(), () =>
                    Column({ style: { alignItems: "stretch", gap: 12, width: "100%" } },
                        Text("JOIN YOUR FRIEND", { style: { fontSize: 14, letterSpacing: 2, color: "#a4e7ce", textAlign: "center" } }),
                        TextInput({ bindValue: joinCode, placeholder: "Six-character code", maxLength: 6, style: { textTransform: "uppercase" } }),
                        Button({ style: { minHeight: 48 }, on: { activate: join } }, "Join airport"),
                        Button({ style: { minHeight: 40 }, on: { activate: () => { view.set("menu"); error.set(""); } } }, "Back"),
                    )),
            )));

    if (invitedCode) queueMicrotask(join);
    return ui;
}
